<template>
  <div class="quick-actions">

    <!-- Header row — click anywhere to toggle -->
    <div
      @click="open = !open"
      class="quick-actions__header"
      :class="{ 'quick-actions__header--open': open }"
    >
      <div class="quick-actions__title-row">
        <Icon name="bolt" :size="14" color="var(--primary)" />
        <span class="quick-actions__title">Pedido rápido</span>
        <span class="quick-actions__subtitle">· Repite el último pedido en segundos</span>
      </div>
      <Icon
        name="chevD"
        :size="14"
        color="var(--text-3)"
        class="quick-actions__chevron"
        :class="{ 'quick-actions__chevron--open': open }"
      />
    </div>

    <!-- Collapsible cards row -->
    <div v-show="open" class="quick-actions__body">

      <!-- Repetir todo button -->
      <button @click.stop="repeatAll" class="quick-repeat-all">
        <div class="quick-repeat-all__label">
          <Icon name="refresh" :size="12" color="#fff" /> Repetir ultimo (todos)
        </div>
        <div class="quick-repeat-all__title">
          Reaplicar el último pedido en {{ activeIds.length }} comedor{{ activeIds.length !== 1 ? 'es' : '' }}
        </div>
        <div class="quick-repeat-all__desc">
          Llena cantidades por comedor con el último pedido registrado.
        </div>
      </button>

      <!-- Per-comedor last order cards -->
      <div
        v-for="{ cid, name, date } in lastOrderCards"
        :key="cid"
        class="quick-card"
      >
        <div class="quick-card__header">
          <span class="quick-card__name">{{ name }}</span>
          <span class="quick-card__date">{{ date }}</span>
        </div>
        <div class="quick-card__desc">Último pedido registrado</div>
        <button @click.stop="$emit('apply-last-order', cid)" class="quick-card__btn">
          <Icon name="refresh" :size="12" color="var(--primary)" /> Repetir en {{ name }}
        </button>
      </div>

    </div>

  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import Icon from './ui/Icon.vue'

const props = defineProps({
  activeIds: { type: Array,  required: true },
  store:     { type: Object, required: true },
})

const emit = defineEmits(['apply-last-order'])

const open = ref(false)

const lastOrderCards = computed(() =>
  props.activeIds
    .filter(cid => props.store.lastOrders[cid])
    .map(cid => {
      const center = props.store.getCenterById(cid)
      const lo     = props.store.lastOrders[cid] || {}
      return { cid, name: center?.name ?? String(cid), date: lo.date || '' }
    })
)

function repeatAll() {
  props.activeIds.forEach(cid => emit('apply-last-order', cid))
}
</script>

<style scoped>
/* ─── Container ──────────────────────────────────────────────────────────────── */
.quick-actions {
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

/* ─── Header ─────────────────────────────────────────────────────────────────── */
.quick-actions__header {
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.quick-actions__header--open {
  padding-bottom: 10px;
}

.quick-actions__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quick-actions__title {
  font-family: var(--ff-head);
  font-size: 13px;
  color: var(--text);
  font-weight: 700;
  letter-spacing: -0.1px;
}

.quick-actions__subtitle {
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-3);
}

.quick-actions__chevron {
  display: block;
  transition: transform 0.2s;
  transform: rotate(-90deg);
}

.quick-actions__chevron--open {
  transform: rotate(0deg);
}

/* ─── Body (scrollable cards row) ────────────────────────────────────────────── */
.quick-actions__body {
  padding: 0 24px 14px;
  display: flex;
  gap: 10px;
  overflow-x: auto;
}

/* ─── Repeat-all card ────────────────────────────────────────────────────────── */
.quick-repeat-all {
  flex: 0 0 auto;
  min-width: 200px;
  padding: 12px 14px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quick-repeat-all__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--ff-head);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  opacity: 0.85;
}

.quick-repeat-all__title {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  letter-spacing: -0.1px;
}

.quick-repeat-all__desc {
  font-family: var(--ff-body);
  font-size: 11px;
  opacity: 0.85;
}

/* ─── Per-comedor card ───────────────────────────────────────────────────────── */
.quick-card {
  flex: 0 0 auto;
  width: 240px;
  padding: 12px 14px;
  background: var(--bg);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.quick-card__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.quick-card__name {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
}

.quick-card__date {
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-3);
}

.quick-card__desc {
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-2);
  line-height: 1.4;
}

.quick-card__btn {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  background: var(--primary-soft);
  color: var(--primary);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 13px;
}
</style>
